import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BubbleBadge,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { find, findSchedules } from '@go-client/lib/api/reservable';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { mapBy, notFalsy, onlyUnique } from '@go-shared/lib/functional';
import { canHostEvents, canManageAllReservations } from '@go-shared/lib/permissions';
import { snapshotFromEventTemplate } from '@go-shared/lib/reserve';
import { safeIdCompare } from '@go-shared/lib/utils';
import {
  ReservableEventTemplate,
  ReservableEventTemplateSnapshot,
} from '@go-shared/types/reservable';
import { ReserveSchedule } from '@go-shared/types/reserveSchedule';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';
import { RESERVABLE_SCHEDULE_BREADCRUMBS } from '../../ReservableSchedulePath/ReservableSchedulePath';

interface SelectEventPagePropsBase {
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  selectedEventSnapshot?: ReservableEventTemplateSnapshot;
  setSelectedEventSnapshot: (eventSnapshot: ReservableEventTemplateSnapshot) => void;
}

interface EventTableItem {
  id: string;
  title: string;
  description: string;
  hostCount: number;
  scheduleCount: number;
  ticketsCount: number;
  packagesCount: number;
  addonsCount: number;
  _data: ReservableEventTemplate;
}
type SelectEventPageProps = SelectEventPagePropsBase &
  FormPageComponentHelpfulProps<RESERVABLE_SCHEDULE_BREADCRUMBS>;

export default function SelectEventPage(props: SelectEventPageProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [dataBundle, setDataBundle] = useState<
    { reservables: ReservableEventTemplate[]; schedules: ReserveSchedule[] } | undefined
  >(undefined);

  async function fetchData() {
    setLoading(true);

    try {
      const [reservables, schedules] = await Promise.all([
        find(activeProperty._id),
        findSchedules(activeProperty._id),
      ]);
      setDataBundle({ reservables, schedules });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty._id]);

  const schedulesMap = mapBy(dataBundle?.schedules || [], s => s.eventTemplate._id);

  const allowedReservables = (dataBundle?.reservables ?? []).filter(r =>
    canManageAllReservations(user)
      ? true
      : canHostEvents(user)
        ? schedulesMap[r._id]?.rules.some(rule => safeIdCompare(rule.host?._id, user._id))
        : false
  );

  const fuse = useMemo(() => {
    return new Fuse(allowedReservables, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['title'],
    });
  }, [allowedReservables]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredItems = fuseSearchResults || allowedReservables;

  const tableItems: EventTableItem[] = filteredItems.map(et => ({
    id: et._id,
    title: et.title,
    description: et.description,
    hostCount:
      schedulesMap[et._id]?.rules
        .map(r => r.host)
        .filter(notFalsy)
        .filter(onlyUnique).length ?? 0,
    scheduleCount: schedulesMap[et._id]?.rules.filter(r => !r.hidden).length ?? 0,
    ticketsCount: et.reservables.tickets.length,
    packagesCount: et.reservables.packages.length,
    addonsCount: et.reservables.addons.length,
    _data: et,
  }));

  const tableFields: BubbleTableFields<EventTableItem>[] = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'title',
    },
    {
      key: 'description',
    },
    {
      key: 'host_schedules',
      label: 'ho./sch.',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'stats',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: BubbleTableTemplate<EventTableItem>[] = [
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          appearance="rounded"
          inputClassName="stretched-link bg-white"
          checked={props.selectedEventSnapshot?.templateId === item._data._id}
          onValue={() => props.setSelectedEventSnapshot(snapshotFromEventTemplate(item._data))}
        />
      ),
    },
    {
      key: 'host_schedules',
      render: item => (
        <div className="flex items-center">
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={item.hostCount > 0 ? 'grape' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="user" />
            <span>{item.hostCount}</span>
          </BubbleBadge>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={item.scheduleCount > 0 ? 'emerald' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="calendar-lines" />
            <span>{item.scheduleCount}</span>
          </BubbleBadge>
        </div>
      ),
    },
    {
      key: 'stats',
      render: item => (
        <div className="flex items-center">
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={item.ticketsCount > 0 ? 'lime' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="ticket" />
            <span>{item.ticketsCount}</span>
          </BubbleBadge>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={item.packagesCount > 0 ? 'banana' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="box-open" />
            <span>{item.packagesCount}</span>
          </BubbleBadge>
          <BubbleBadge
            className="mt-1 mr-1 whitespace-nowrap-i"
            variant={item.addonsCount > 0 ? 'ocean' : 'smoke'}
          >
            <FontAwesomeIcon fixedWidth className="mr-1" icon="puzzle-piece" />
            <span>{item.addonsCount}</span>
          </BubbleBadge>
        </div>
      ),
    },
  ];

  const forwardDisabled = !props.selectedEventSnapshot;

  return (
    <div className="eaf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate-dark mr-auto"
              items={[{ label: 'Experience Selected', value: props.selectedEventSnapshot?.title }]}
            />
            <BubbleFormActions
              isBusy={props.isBusy}
              forwardTitle={props.forwardTitle || 'Next: Enter Schedules'}
              backTitle={props.backTitle}
              onGoForward={props.onGoForward}
              onGoBack={props.onGoBack}
              forwardDisabled={forwardDisabled}
            />
          </>
        )}
      >
        <BubbleFormHeader<RESERVABLE_SCHEDULE_BREADCRUMBS>
          title={props.title || 'Create Experience'}
          subtitle={props.subtitle || 'Select Experience'}
          pageIndex={props.pageIndex ?? 1}
          onClose={props.onClose}
          breadcrumbs={props.breadcrumbs}
          activeBreadcrumb="event"
        />
        <BubbleOverlay className="flex-grow -mx-8 -mb-2" loading={loading}>
          <div className="pb-2 px-8">
            <BubbleTable
              headVariant="titanium"
              stickyHeader={0}
              striped
              responsive
              keyExtractor={item => item._data._id}
              items={tableItems}
              fields={tableFields}
              templates={tableTemplates}
              showPretitle
              pretitleClassName="p-0-i"
              onRowClick={item =>
                props.setSelectedEventSnapshot(snapshotFromEventTemplate(item._data))
              }
              pretitleRender={() => (
                <div className="px-4 pt-4 mb-1 flex items-center">
                  <BubbleInput
                    variant="titanium"
                    block
                    type="text"
                    value={search}
                    placeholder="Search..."
                    className="flex-grow"
                    onValue={setSearch}
                  />
                </div>
              )}
            />
          </div>
        </BubbleOverlay>
      </FormGenericContainer>
    </div>
  );
}
